"use client";

import { Badge } from "../../";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../";
import { cn } from "../../utils";
import type { DynamicToolUIPart, ToolUIPart } from "ai";
import {
  IconCircleCheck,
  IconChevronDown,
  IconCircle,
  IconClock,
  IconTool,
  IconCircleX,
} from "@tabler/icons-react";
import type { ComponentProps, ReactNode } from "react";
import { isValidElement } from "react";
import { AiCodeBlock as CodeBlock } from "../code-block/code-block";

export type ToolProps = ComponentProps<typeof Collapsible>;

export const Tool = ({ className, ...props }: ToolProps) => (
  <Collapsible
    className={cn("al-group al-not-prose al-mb-4 al-w-full al-rounded-md al-border", className)}
    {...props}
  />
);

export type ToolPart = ToolUIPart | DynamicToolUIPart;

export type ToolHeaderProps = {
  title?: string;
  className?: string;
} & (
  | { type: ToolUIPart["type"]; state: ToolUIPart["state"]; toolName?: never }
  | {
      type: DynamicToolUIPart["type"];
      state: DynamicToolUIPart["state"];
      toolName: string;
    }
);

export const getStatusBadge = (status: ToolPart["state"]) => {
  const labels: Record<ToolPart["state"], string> = {
    "input-streaming": "Pending",
    "input-available": "Running",
    "approval-requested": "Awaiting Approval",
    "approval-responded": "Responded",
    "output-available": "Completed",
    "output-error": "Error",
    "output-denied": "Denied",
  };

  const icons: Record<ToolPart["state"], ReactNode> = {
    "input-streaming": <IconCircle className="al-size-4" />,
    "input-available": <IconClock className="al-size-4 al-animate-pulse" />,
    "approval-requested": <IconClock className="al-size-4 al-text-yellow-600" />,
    "approval-responded": <IconCircleCheck className="al-size-4 al-text-blue-600" />,
    "output-available": <IconCircleCheck className="al-size-4 al-text-green-600" />,
    "output-error": <IconCircleX className="al-size-4 al-text-red-600" />,
    "output-denied": <IconCircleX className="al-size-4 al-text-orange-600" />,
  };

  return (
    <Badge className="al-gap-1.5 al-rounded-full al-text-xs" variant="secondary">
      {icons[status]}
      {labels[status]}
    </Badge>
  );
};

export const ToolHeader = ({
  className,
  title,
  type,
  state,
  toolName,
  ...props
}: ToolHeaderProps) => {
  const derivedName = type === "dynamic-tool" ? toolName : type.split("-").slice(1).join("-");

  return (
    <CollapsibleTrigger
      className={cn(
        "al-flex al-w-full al-items-center al-justify-between al-gap-4 al-p-3",
        className
      )}
      {...props}
    >
      <div className="al-flex al-items-center al-gap-2">
        <IconTool className="al-size-4 al-text-muted-foreground" />
        <span className="al-font-medium al-text-sm">{title ?? derivedName}</span>
        {getStatusBadge(state)}
      </div>
      <IconChevronDown className="al-size-4 al-text-muted-foreground al-transition-transform group-data-[state=open]:al-rotate-180" />
    </CollapsibleTrigger>
  );
};

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
  <CollapsibleContent
    className={cn(
      "data-[state=closed]:al-fade-out-0 data-[state=closed]:al-slide-out-to-top-2 data-[state=open]:al-slide-in-from-top-2 al-text-popover-foreground al-outline-none data-[state=closed]:al-animate-out data-[state=open]:al-animate-in",
      className
    )}
    {...props}
  />
);

export type ToolInputProps = ComponentProps<"div"> & {
  input: ToolPart["input"];
};

export const ToolInput = ({ className, input, ...props }: ToolInputProps) => (
  <div className={cn("al-space-y-2 al-overflow-hidden al-p-4", className)} {...props}>
    <h4 className="al-font-medium al-text-muted-foreground al-text-xs al-uppercase al-tracking-wide">
      Parameters
    </h4>
    <div className="al-rounded-md al-bg-muted/50">
      <CodeBlock code={JSON.stringify(input, null, 2)} language="json" />
    </div>
  </div>
);

export type ToolOutputProps = ComponentProps<"div"> & {
  output: ToolPart["output"];
  errorText: ToolPart["errorText"];
};

export const ToolOutput = ({ className, output, errorText, ...props }: ToolOutputProps) => {
  if (!(output || errorText)) {
    return null;
  }

  let Output = <div>{output as ReactNode}</div>;

  if (typeof output === "object" && !isValidElement(output)) {
    Output = <CodeBlock code={JSON.stringify(output, null, 2)} language="json" />;
  } else if (typeof output === "string") {
    Output = <CodeBlock code={output} language="json" />;
  }

  return (
    <div className={cn("al-space-y-2 al-p-4", className)} {...props}>
      <h4 className="al-font-medium al-text-muted-foreground al-text-xs al-uppercase al-tracking-wide">
        {errorText ? "Error" : "Result"}
      </h4>
      <div
        className={cn(
          "al-overflow-x-auto al-rounded-md al-text-xs [&_table]:al-w-full",
          errorText
            ? "al-bg-destructive/10 al-text-destructive"
            : "al-bg-muted/50 al-text-foreground"
        )}
      >
        {errorText && <div>{errorText}</div>}
        {Output}
      </div>
    </div>
  );
};
